import { useCallback, useRef, type KeyboardEvent } from 'react';
import { PhaseSelect } from '../../Inputs/PhaseSelect/PhaseSelect';
import { NutrientInput } from '../../Inputs/NutrientInput/NutrientInput';
import {
	MONTHS,
	MONTH_LABELS,
	NUTRIENTS,
	NUTRIENT_LABELS,
	HARVEST_PHASE_LABELS,
	type HarvestPhase,
	type Nutrient,
} from '../../../constants';
import { calculateNutrientSum } from '../../../utils';
import type { ParcelData } from '../../../types';
import styles from './NutrientTable.module.css';

interface NutrientTableProps {
	data: ParcelData;
	onChange?: (data: ParcelData) => void;
	isEditing?: boolean;
}

const TOTAL_ROWS = MONTHS.length;
const TOTAL_COLS = NUTRIENTS.length;

export const NutrientTable = ({ data, onChange, isEditing = false }: NutrientTableProps) => {
	const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

	const registerRef = useCallback((row: number, col: number, el: HTMLInputElement | null) => {
		const key = `${row}-${col}`;
		if (el) {
			inputRefs.current.set(key, el);
		} else {
			inputRefs.current.delete(key);
		}
	}, []);

	const focusCell = useCallback((row: number, col: number) => {
		if (row < 0 || row >= TOTAL_ROWS || col < 0 || col >= TOTAL_COLS) return;
		const input = inputRefs.current.get(`${row}-${col}`);
		if (input) {
			input.focus();
			input.select();
		}
	}, []);

	const handleCellKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			const target = e.currentTarget;
			const row = Number(target.dataset.row);
			const col = Number(target.dataset.col);

			switch (e.key) {
				case 'ArrowUp':
					e.preventDefault();
					focusCell(row - 1, col);
					break;
				case 'ArrowDown':
				case 'Enter':
					e.preventDefault();
					focusCell(row + 1, col);
					break;
				case 'ArrowLeft':
					e.preventDefault();
					focusCell(row, col - 1);
					break;
				case 'ArrowRight':
					e.preventDefault();
					focusCell(row, col + 1);
					break;
				case 'Tab':
					e.preventDefault();
					if (e.shiftKey) {
						if (col > 0) {
							focusCell(row, col - 1);
						} else if (row > 0) {
							focusCell(row - 1, TOTAL_COLS - 1);
						}
					} else {
						if (col < TOTAL_COLS - 1) {
							focusCell(row, col + 1);
						} else if (row < TOTAL_ROWS - 1) {
							focusCell(row + 1, 0);
						}
					}
					break;
				default:
					return;
			}
		},
		[focusCell]
	);

	const handlePhaseChange = useCallback(
		(monthIndex: number, phase: HarvestPhase | '') => {
			if (!onChange || phase === '') return;
			const newPhases = [...data.phase];
			newPhases[monthIndex] = phase as HarvestPhase;
			onChange({
				...data,
				phase: newPhases,
			});
		},
		[data, onChange]
	);

	const handleNutrientChange = useCallback(
		(nutrient: Nutrient, monthIndex: number, value: number) => {
			if (!onChange) return;
			const newValues = [...data[nutrient]];
			newValues[monthIndex] = value;
			onChange({
				...data,
				[nutrient]: newValues,
			});
		},
		[data, onChange]
	);

	const getNutrientSum = (nutrient: Nutrient): number => {
		return calculateNutrientSum(data[nutrient]);
	};

	return (
		<div className={styles.container}>
			<div className={styles.tableWrapper}>
				<table className={styles.table}>
					<thead>
						<tr>
							<th className={styles.headerCell}>Mês</th>
							<th className={styles.headerCell}>Fase</th>
							{NUTRIENTS.map((nutrient) => (
								<th
									key={nutrient}
									className={`${styles.headerCell} ${styles.nutrientHeaderCell}`}
								>
									{NUTRIENT_LABELS[nutrient]}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{MONTHS.map((month, monthIndex) => (
							<tr key={month} className={styles.row}>
								<td className={styles.monthCell}>{MONTH_LABELS[month]}</td>
								<td className={styles.phaseCell}>
									{isEditing ? (
										<PhaseSelect
											value={data.phase[monthIndex]}
											onChange={(phase) => handlePhaseChange(monthIndex, phase)}
										/>
									) : (
										<span className={styles.phaseValue}>
											{data.phase[monthIndex]
												? HARVEST_PHASE_LABELS[data.phase[monthIndex] as HarvestPhase]
												: 'Selecione...'}
										</span>
									)}
								</td>
								{NUTRIENTS.map((nutrient, nutrientIndex) => (
									<td key={nutrient} className={styles.nutrientCell}>
										{isEditing ? (
											<NutrientInput
												ref={(el) => registerRef(monthIndex, nutrientIndex, el)}
												value={data[nutrient][monthIndex]}
												onChange={(value) => handleNutrientChange(nutrient, monthIndex, value)}
												onKeyDown={handleCellKeyDown}
												data-row={monthIndex}
												data-col={nutrientIndex}
											/>
										) : (
											<span className={styles.nutrientValue}>
												{Number.isNaN(data[nutrient][monthIndex])
													? '---'
													: data[nutrient][monthIndex]}
											</span>
										)}
									</td>
								))}
							</tr>
						))}
					</tbody>
					<tfoot>
						<tr className={styles.sumRow}>
							<td colSpan={1}></td>
							<td className={styles.sumCell} colSpan={1}>
								Soma Anual
							</td>
							{NUTRIENTS.map((nutrient) => (
								<td key={nutrient} className={styles.sumValueCell}>
									{getNutrientSum(nutrient).toFixed(1)}
								</td>
							))}
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
};
